import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

// Import your shared components
import { TextInput } from "./TextInput";
import { CvEditorSession } from "../types/cv-editor.types";
import Button from "../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../components/button/types";

// Define the shape of a single position for both the list and the form
interface PositionItem {
  id: string; // A temporary client-side ID for list management
  company: string;
  position: string;
  location: string;
  date: string;
  summary: string;
}

// The data our form will manage
type PositionFormData = Omit<PositionItem, "id">;

interface PositionsValidationStepProps {
  session: CvEditorSession;
  onConfirm: (positionsData: PositionFormData[]) => void;
  isLoading: boolean;
}

const EMPTY_FORM: PositionFormData = {
  company: "",
  position: "",
  location: "",
  date: "",
  summary: "",
};

export const PositionsValidationStep: React.FC<
  PositionsValidationStepProps
> = ({ session, onConfirm, isLoading }) => {
  const { t } = useTranslation("cvEditor");

  // State for the list of positions
  const [positions, setPositions] = useState<PositionItem[]>([]);
  // State to manage which position is being edited, or 'new' for adding
  const [editingId, setEditingId] = useState<string | null>(null);

  const isFormOpen = editingId !== null;

  // Initialize react-hook-form for the Add/Edit form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<PositionFormData>({
    mode: "onChange",
    defaultValues: EMPTY_FORM,
  });

  // Pre-fill the list from session data when the component mounts
  useEffect(() => {
    const initialPositions =
      session.parsedCvData?.sections?.experience?.items || [];
    const formattedPositions = initialPositions.map(
      (p: any, index: number) => ({
        id: `pos-${index}-${Date.now()}`, // Create a stable unique ID
        company: p.company || "",
        position: p.position || "",
        location: p.location || "",
        date: p.date || "",
        summary: p.summary || "",
      })
    );
    setPositions(formattedPositions);

    // If the user has no positions, automatically open the "Add" form
    if (formattedPositions.length === 0) {
      setEditingId("new");
      reset(EMPTY_FORM);
    }
  }, [session, reset]);

  // --- Form and List Management ---

  const handleOpenAddForm = () => {
    reset(EMPTY_FORM);
    setEditingId("new");
  };

  const handleOpenEditForm = (pos: PositionItem) => {
    reset(pos); // Pre-fill the form with the data of the item being edited
    setEditingId(pos.id);
  };

  const handleCancelForm = () => {
    setEditingId(null);
    reset(EMPTY_FORM);
  };

  const handleDeletePosition = (idToDelete: string) => {
    setPositions((prev) => prev.filter((p) => p.id !== idToDelete));
  };

  const onSaveForm = (data: PositionFormData) => {
    if (editingId === "new") {
      // Add a new position to the list
      setPositions((prev) => [...prev, { ...data, id: `new-${Date.now()}` }]);
    } else {
      // Update an existing position in the list
      setPositions((prev) =>
        prev.map((p) => (p.id === editingId ? { id: p.id, ...data } : p))
      );
    }
    handleCancelForm(); // Close the form
  };

  // The final confirmation sends the clean data (without client-side IDs)
  const handleConfirmAndNext = () => {
    const dataToSubmit = positions.map(({ id, ...rest }) => rest);
    onConfirm(dataToSubmit);
  };

  // --- Render Functions ---

  const renderForm = () => (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
      <h3 className="text-md font-semibold text-gray-800 mb-4">
        {editingId === "new"
          ? t("positionsStep.addTitle")
          : t("positionsStep.editTitle")}
      </h3>
      <form
        onSubmit={handleSubmit(onSaveForm)}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <Controller
          name="position"
          control={control}
          rules={{ required: t("positionsStep.errors.required") as string }}
          render={({ field }) => (
            <TextInput
              label={t("positionsStep.positionLabel")}
              {...field}
              required
              error={!!errors.position}
              helperText={errors.position?.message}
              placeholder={
                t("positionsStep.inputPlaceholder") || "Write here.."
              }
            />
          )}
        />
        <Controller
          name="company"
          control={control}
          rules={{ required: t("positionsStep.errors.required") as string }}
          render={({ field }) => (
            <TextInput
              label={t("positionsStep.companyLabel")}
              {...field}
              required
              error={!!errors.company}
              helperText={errors.company?.message}
              placeholder={
                t("positionsStep.inputPlaceholder") || "Write here.."
              }
            />
          )}
        />
        <Controller
          name="location"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t("positionsStep.locationLabel")}
              {...field}
              placeholder={
                t("positionsStep.inputPlaceholder") || "Write here.."
              }
            />
          )}
        />
        <Controller
          name="date"
          control={control}
          rules={{ required: t("positionsStep.errors.required") as string }}
          render={({ field }) => (
            <TextInput
              label={t("positionsStep.dateLabel")}
              {...field}
              required
              error={!!errors.date}
              helperText={errors.date?.message}
              placeholder={
                t("positionsStep.inputPlaceholder") || "Write here.."
              }
            />
          )}
        />
        <div className="md:col-span-2 flex justify-end gap-3 mt-2 text-sm">
          <Button
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={handleCancelForm}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            disabled={!isValid}
          >
            {editingId === "new"
              ? t("positionsStep.addButton")
              : t("positionsStep.saveButton")}
          </Button>
        </div>
      </form>
    </div>
  );

  const renderPositionsList = () => (
    <>
      <div className="border border-gray-200 rounded-lg">
        {positions.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {positions.map((pos) => (
              <li
                key={pos.id}
                className="px-4 py-3 flex items-center justify-between hover:bg-gray-50"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">{`${pos.position} at ${pos.company}`}</p>
                  <p className="text-sm text-gray-500">{`${pos.date} - ${
                    pos.location || "N/A"
                  }`}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenEditForm(pos)}
                    className="p-1 text-gray-400 hover:text-black"
                  >
                    <EditIcon sx={{ fontSize: 20 }} />
                  </button>
                  <button
                    onClick={() => handleDeletePosition(pos.id)}
                    className="p-1 text-gray-400 hover:text-red-800"
                  >
                    <DeleteIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center p-8 text-gray-500">
            {t("positionsStep.noPositions")}
          </div>
        )}
      </div>
      <div className="text-center mt-4 mb-4 text-sm">
        <Button
          variant={ButtonVariant.SECONDARY}
          size={ButtonSize.SMALL}
          onClick={handleOpenAddForm}
          className="inline-flex items-center"
        >
          <AddIcon sx={{ fontSize: 16, marginRight: 1 }} />
          {t("positionsStep.addAnotherButton")}
        </Button>
      </div>
    </>
  );

  return (
    <div className="p-6 flex flex-col h-full">
      <div className="flex-grow overflow-y-auto pr-2 -mr-2">
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {t("positionsStep.title")}
        </h3>
        <p className="text-gray-600 mb-4">{t("positionsStep.description")}</p>
        {isFormOpen ? renderForm() : renderPositionsList()}
      </div>
      <div className="flex-shrink-0 mt-auto pt-4 border-t border-gray-200">
        <div className="flex justify-end">
          <Button
            type="button"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            onClick={handleConfirmAndNext}
            disabled={positions.length === 0 || isLoading || isFormOpen}
            isLoading={isLoading}
            className="w-full"
          >
            {t("common.next")}
          </Button>
        </div>
      </div>
    </div>
  );
};
